import React, {useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useApiService} from '../../context/api/useApiContext';
import {IPost, IPostCreateRequest} from '../../types/resources/Post';
import {PostType} from "../../enums/PostType";
import TextArea from '../Forms/TextArea';
import TextInput from '../Forms/TextInput';
import Checkbox from '../Forms/Checkbox';
import {IOnlineCondolenceSummary} from '../../types/resources/OnlineCondolenceSummary';
import {usePostsContext} from '../../context/posts/usePostsContext';
import FileInput from '../Forms/FileInput';
import {useGoogleReCaptcha} from 'react-google-recaptcha-v3';
import {useSettingsContext} from '../../context/settings/useSettingsContext';

export type PostFormProps = {
    onlineCondolence: IOnlineCondolenceSummary,
    postType: PostType,
    handleFormVisibility: (isVisible: boolean) => void;
    onPostCreated: (post: IPost) => void;
}

const PostForm: React.FC<PostFormProps> = ({ onlineCondolence, postType, handleFormVisibility, onPostCreated }) => {
    const { t } = useTranslation();
    const { executeRecaptcha } = useGoogleReCaptcha();
    const { useGoogleRecaptchaV3, privacyPolicyUrl } = useSettingsContext();
    const { apiService, companyInfoId } = useApiService();
    const { postsCount, setPostsCount } = usePostsContext();

    const [authorTitle, setAuthorTitle] = useState<string>('');
    const [authorName, setAuthorName] = useState('');
    const [authorEmail, setAuthorEmail] = useState('');
    const [authorCity, setAuthorCity] = useState('');
    const [message, setMessage] = useState('');
    const [mediaUrl, setMediaUrl] = useState('https://');
    const [file, setFile] = useState<File|null>(null);
    const [isPrivate, setIsPrivate] = useState(false);
    const [consent, setConsent] = useState(false);

    const resetForm = () => {
        setAuthorTitle('');
        setAuthorName('');
        setAuthorEmail('');
        setAuthorCity('');
        setMessage('');
        setFile(null);
        setIsPrivate(false);
    };

    const convertFileToBase64 = (file: File): Promise<{ fileName: string, fileDataBase64: string }> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();

            reader.onload = (event) => {
                const arrayBuffer = event.target?.result as ArrayBuffer;
                const byteArray = new Uint8Array(arrayBuffer);
                const fileData = btoa(Array.from(byteArray, byte => String.fromCharCode(byte)).join(''));

                resolve({fileName: file.name, fileDataBase64: fileData});
            };

            reader.onerror = (error) => {
                console.error('Error reading file:', error);
                reject(error);
            };

            reader.readAsArrayBuffer(file);
        });
    };

    const handlePostSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        try {
            let fileName = '';
            let fileDataBase64 = '';

            if (file) {
                const fileConversionResult = await convertFileToBase64(file);

                fileName = fileConversionResult.fileName;
                fileDataBase64 = fileConversionResult.fileDataBase64;
            }

            const postData: IPostCreateRequest = {
                companyInfoId: onlineCondolence.companyInfoId ?? companyInfoId,
                onlineCondolenceId: onlineCondolence.id,

                authorTitle: authorTitle,
                authorName: authorName,
                authorEmail: authorEmail,
                authorCity: authorCity,
                message: message,
                mediaUrl: ([PostType.MessageWithAudioLink, PostType.MessageWithVideoLink].includes(postType))
                    ? mediaUrl
                    : undefined,
                uploadedFileName: fileName,
                uploadedFileDataBase64: fileDataBase64,
                type: postType,
                isPrivate: isPrivate,
            };

            if (useGoogleRecaptchaV3) {
                if (!executeRecaptcha) {
                    console.log('Execute recaptcha not yet available');
                    return;
                }

                postData.recaptchaToken = await executeRecaptcha('handlePostSubmit');
            }

            const responseData = await apiService.createPost(postData);

            onPostCreated(responseData);
            setPostsCount(postsCount + 1);
            handleFormVisibility(false);
            resetForm();
        } catch (error) {
            console.log(error);
            // TODO: Inline error messages
        }
    };

    const handleCancel = () => {
        handleFormVisibility(false);
    }

    const mediaUrlLabel = (postType === PostType.MessageWithAudioLink)
        ? 'music-url'
        : (postType === PostType.MessageWithVideoLink)
            ? 'video-url'
            : 'media-url'
    ;

    return (
        <div className='cr-post-form cr-flex cr-flex-col cr-gap cr-center cr-mt-lg cr-mb-xl'>
            {/* TODO novalidate */}
            <form className='cr-flex cr-flex-col cr-gap' onSubmit={handlePostSubmit}>
                <TextInput
                    label={t('title')}
                    name={'title'}
                    value={authorTitle}
                    setValue={setAuthorTitle}
                />
                <TextInput
                    label={t('name')}
                    name={'name'}
                    value={authorName}
                    setValue={setAuthorName}
                    isRequired={true}
                />
                <TextInput
                    label={t('email')}
                    name={'email'}
                    type={'email'}
                    value={authorEmail}
                    setValue={setAuthorEmail}
                    isRequired={false}
                />
                <TextInput
                    label={t('city')}
                    name={'city'}
                    value={authorCity}
                    setValue={setAuthorCity}
                    isRequired={false}
                />
                <TextArea
                    text={message}
                    setText={setMessage}
                    placeholder=' '
                    label={t('your-message')}
                    isRequired={true}
                />
                {[PostType.MessageWithAudioLink, PostType.MessageWithVideoLink].includes(postType) &&
                    <TextInput
                        label={t(mediaUrlLabel)}
                        name={'mediaUrl'}
                        type={'url'}
                        value={mediaUrl}
                        setValue={setMediaUrl}
                        isRequired={true}
                    />
                }
                {postType === PostType.MessageWithPhoto &&
                    <FileInput
                        label={t('choose-file')}
                        name={'file'}
                        allowedFileTypes=".jpg,.jpeg,.png,.gif,.webp,.heic"
                        setValue={setFile}
                        isRequired={false}
                    />
                }
                <div className='cr-flex'>
                    <Checkbox
                        label={t('make-this-post-private')}
                        name={'privatePost'}
                        checked={isPrivate}
                        setValue={setIsPrivate}
                    />
                </div>
                {privacyPolicyUrl &&
                    <div className="cr-flex">
                        <Checkbox
                            label={t('consent-to-privacy-policy')}
                            name={'consent'}
                            checked={consent}
                            setValue={setConsent}
                            isRequired={true}
                        >
                            <a href={privacyPolicyUrl} target={'_blank'}>{t('privacy-policy-link-text')}</a>
                        </Checkbox>
                    </div>
                }
                <div className="cr-flex cr-gap cr-center cr-mt">
                    <button
                        type="submit"
                        className="cr-pill cr-bg-alert cr-text-white cr-text-base"
                    >
                        {t('send-your-message')}
                    </button>
                    <button
                        type="button"
                        className='cr-cancel cr-pill cr-border-primary cr-text-primary cr-text-base'
                        onClick={handleCancel}
                    >
                        {t('cancel')}
                    </button>
                </div>
            </form>
        </div>
    );
}

export default PostForm;
